import { ButtonBase, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { InvitationDecisions } from './useInvitationDecisions';

interface Props {
  decisions: InvitationDecisions;
  /** `dark` sits on the navy surface, `light` on cream. */
  tone?: 'light' | 'dark';
  centered?: boolean;
}

/** Pills naming every pending clinic; hidden for a single invitation. */
export default function InvitationSwitcher({
  decisions: { pending, currentIndex, select, action },
  tone = 'light',
  centered = false,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  if (pending.length < 2) return null;
  return (
    <Stack
      component="nav"
      aria-label={String(t('invitationAcceptance.pending'))}
      direction="row"
      alignItems="center"
      justifyContent={centered ? 'center' : 'flex-start'}
      flexWrap="wrap"
      gap={1}
    >
      {pending.map((invite, index) => {
        const selected = index === currentIndex;
        return (
          <ButtonBase
            key={invite.id}
            disabled={Boolean(action)}
            aria-pressed={selected}
            onClick={() => select(index)}
            sx={(theme) => ({
              px: 2,
              py: 1,
              borderRadius: 99,
              typography: 'subtitle2',
              border: 1,
              transition: theme.transitions.create([
                'background-color',
                'color',
              ]),
              ...(tone === 'light'
                ? {
                    borderColor: selected ? 'text.primary' : 'divider',
                    bgcolor: selected ? 'text.primary' : 'background.default',
                    color: selected ? 'background.default' : 'text.primary',
                  }
                : {
                    borderColor: alpha(theme.palette.common.white, 0.16),
                    bgcolor: selected
                      ? 'common.white'
                      : alpha(theme.palette.common.white, 0.06),
                    color: selected ? 'text.primary' : 'common.white',
                  }),
            })}
          >
            {invite.organizationName || t('invitationAcceptance.organization')}
          </ButtonBase>
        );
      })}
      <Typography
        variant="caption"
        aria-live="polite"
        sx={{ px: 1, color: tone === 'light' ? 'text.secondary' : 'grey.500' }}
      >
        {t('invitationAcceptance.counter', {
          current: currentIndex + 1,
          total: pending.length,
        })}
      </Typography>
    </Stack>
  );
}
